import type { SlideChallenge } from '@/services/loginVerification';
import { createRandomId, createSecureRandomToken } from '@/utils/randomId';
import type { LoginCaptchaConfig } from './loginCaptchaConfig';
import { createSlideFixture } from './slideFixture';

type SlideFixture = Awaited<ReturnType<typeof createSlideFixture>>;

export class CaptchaError extends Error {
  constructor(public code: string) {
    super(code);
  }
}

/** Browser-only simulation. Not a security boundary or a substitute for Go/Redis. */
export function createLoginCaptcha(
  getPolicy: () => LoginCaptchaConfig,
  fixture: () => SlideFixture | Promise<SlideFixture> = createSlideFixture,
  now = () => Date.now(),
) {
  const challenges = new Map<
    string,
    {
      key: string;
      expires: number;
      used: boolean;
      answer: { x: number; y: number };
    }
  >();
  const proofs = new Map<string, { key: string; expires: number; used: boolean }>();
  const attempts = new Map<
    string,
    { count: number; expires: number; lockedUntil: number }
  >();
  const requests = new Map<string, { count: number; expires: number }>();
  const pendingIssues = new Map<string, symbol>();
  const ttl = 120000;
  function fail(code: string): never {
    throw new CaptchaError(code);
  }
  function key(input: Record<string, unknown>) {
    if (input.type !== 'account' && input.type !== 'mobile') fail('CAPTCHA_INVALID');
    const account = input.type === 'mobile' ? input.mobile : input.username;
    if (typeof account !== 'string' || !account.trim() || account.length > 128)
      fail('CAPTCHA_INVALID');
    return `${input.type}:${account.trim()}`;
  }
  function cleanup() {
    for (const map of [challenges, proofs, requests, attempts]) {
      for (const [id, row] of map) if (row.expires <= now()) map.delete(id);
    }
  }
  function rateLimit(id: string, limit: number) {
    const row = requests.get(id);
    if (row && row.expires > now() && row.count >= limit) fail('CAPTCHA_RATE_LIMITED');
    requests.set(
      id,
      row && row.expires > now()
        ? { ...row, count: row.count + 1 }
        : { count: 1, expires: now() + 60000 },
    );
  }
  function required(id: string) {
    const policy = getPolicy().humanVerification;
    const row = attempts.get(id);
    return (
      policy.mode === 'always' ||
      (policy.mode === 'afterFailures' &&
        (row && row.expires > now() ? row.count : 0) >= policy.failureThreshold)
    );
  }
  function locked(id: string) {
    const row = attempts.get(id);
    return row ? Math.max(0, Math.ceil((row.lockedUntil - now()) / 1000)) : 0;
  }
  return {
    async issue(input: Record<string, unknown>): Promise<SlideChallenge> {
      cleanup();
      if (input.purpose !== 'login') fail('CAPTCHA_PURPOSE');
      const idKey = key(input);
      // Refresh invalidates the previous unsolved challenge for this account.
      for (const row of challenges.values()) if (row.key === idKey) row.used = true;
      const request = Symbol();
      pendingIssues.set(idKey, request);
      try {
        const { data, answer } = await fixture();
        // A slower request must not revive a challenge superseded by refresh.
        if (pendingIssues.get(idKey) !== request) fail('CAPTCHA_USED');
        const id = createRandomId();
        const expires = now() + ttl;
        challenges.set(id, { key: idKey, expires, used: false, answer });
        return {
          ...data,
          id,
          provider: 'go-captcha',
          purpose: 'login',
          expiresAt: new Date(expires).toISOString(),
        };
      } finally {
        if (pendingIssues.get(idKey) === request) pendingIssues.delete(idKey);
      }
    },
    verify(input: Record<string, unknown>) {
      if (input.purpose !== 'login') fail('CAPTCHA_PURPOSE');
      const idKey = key(input);
      rateLimit('verify:all', 60);
      const challenge = challenges.get(String(input.id));
      if (!challenge) fail('CAPTCHA_INVALID');
      if (challenge.expires <= now()) fail('CAPTCHA_EXPIRED');
      if (challenge.used) fail('CAPTCHA_USED');
      if (challenge.key !== idKey) fail('CAPTCHA_CONTEXT');
      // One attempt per image; mock check-and-consume is synchronous.
      challenge.used = true;
      if (
        typeof input.x !== 'number' ||
        typeof input.y !== 'number' ||
        !Number.isFinite(input.x) ||
        !Number.isFinite(input.y) ||
        Math.abs(input.x - challenge.answer.x) > 4 ||
        Math.abs(input.y - challenge.answer.y) > 4
      )
        fail('CAPTCHA_INVALID');
      const token = createSecureRandomToken();
      const expires = now() + ttl;
      proofs.set(token, { key: idKey, expires, used: false });
      return { token, expiresAt: new Date(expires).toISOString() };
    },
    beforeLogin(input: Record<string, unknown>) {
      const idKey = key(input);
      const retryAfter = locked(idKey);
      if (retryAfter)
        return { status: 'locked', verificationError: 'LOGIN_LOCKED', retryAfter };
      rateLimit('login:all', 60);
      const needsCaptcha = required(idKey);
      if (!needsCaptcha && !input.humanVerificationToken) return null;
      let error: string | undefined;
      const proof = proofs.get(String(input.humanVerificationToken));
      if (!input.humanVerificationToken) error = 'CAPTCHA_REQUIRED';
      else if (!proof) error = 'CAPTCHA_INVALID';
      else if (proof.expires <= now()) error = 'CAPTCHA_EXPIRED';
      else if (proof.used) error = 'CAPTCHA_USED';
      else if (proof.key !== idKey) error = 'CAPTCHA_CONTEXT';
      if (error)
        return {
          status: 'verification_required',
          humanVerificationRequired: true,
          verificationError: error,
        };
      if (!proof) fail('CAPTCHA_INVALID');
      proof.used = true;
      return null;
    },
    afterLogin(input: Record<string, unknown>, success: boolean) {
      const idKey = key(input);
      if (success) {
        attempts.delete(idKey);
        for (const [id, proof] of proofs) if (proof.key === idKey) proofs.delete(id);
        return { status: 'ok' };
      }
      const previous = attempts.get(idKey);
      const count = (previous && previous.expires > now() ? previous.count : 0) + 1;
      const policy = getPolicy();
      const lockedUntil =
        count >= policy.maxFailures ? now() + policy.lockMinutes * 60000 : 0;
      attempts.set(idKey, {
        count,
        lockedUntil,
        expires: lockedUntil || now() + 15 * 60000,
      });
      return lockedUntil
        ? {
            status: 'locked',
            verificationError: 'LOGIN_LOCKED',
            retryAfter: locked(idKey),
          }
        : { status: 'error', humanVerificationRequired: required(idKey) };
    },
  };
}
